/**
 * Centralizes reject-reason metadata and validation so every capture and
 * display surface shares the backend contract without duplicating policy.
 */

import type { RejectReason } from 'src/store/generated/leela-api-generated';

export const REJECT_REASON_NOTE_MAX_LENGTH = 500;

export const REJECT_REASONS = [
  'SUBJECT_CUT_OFF',
  'AWKWARD_FRAMING',
  'BLURRY_LOW_RESOLUTION',
  'KEY_ELEMENT_MISSING',
  'WRONG_ARTWORK',
  'SENSITIVE_OFF_BRAND',
  'INCORRECT_MESSAGING',
  'DO_NOT_PROMOTE_TITLE',
  'OTHER',
] as const satisfies readonly RejectReason[];

export const REJECT_REASON_LABEL_KEYS: Record<RejectReason, string> = {
  SUBJECT_CUT_OFF: 'asset_reject_reason_subject_cut_off',
  AWKWARD_FRAMING: 'asset_reject_reason_awkward_framing',
  BLURRY_LOW_RESOLUTION: 'asset_reject_reason_blurry_low_resolution',
  KEY_ELEMENT_MISSING: 'asset_reject_reason_key_element_missing',
  WRONG_ARTWORK: 'asset_reject_reason_wrong_artwork',
  SENSITIVE_OFF_BRAND: 'asset_reject_reason_sensitive_off_brand',
  INCORRECT_MESSAGING: 'asset_reject_reason_incorrect_messaging',
  DO_NOT_PROMOTE_TITLE: 'asset_reject_reason_do_not_promote_title',
  OTHER: 'asset_reject_reason_other',
};

export function isValidRejectReasonNote (note: string | undefined): boolean {
  const trimmedLength = note?.trim().length ?? 0;
  return trimmedLength >= 1 && trimmedLength <= REJECT_REASON_NOTE_MAX_LENGTH;
}

export interface RejectReasonValue {
  readonly reason?: RejectReason;
  readonly note?: string;
}

export function isCompleteRejectReason (
  value: RejectReasonValue | undefined,
): boolean {
  return value?.reason !== undefined &&
    (value.reason !== 'OTHER' || isValidRejectReasonNote(value.note));
}
